/**
 * Arbeitstage-Kalender Bayern (E1-A12) – reiner Rechenkern nach AD-27: keine Datenbank, keine Uhr, kein Netz.
 *
 * - Fachliche Daten sind ISO-Daten `YYYY-MM-DD` (Kalendertag in Europe/Berlin); intern wird in UTC-Tagen gerechnet,
 *   damit Sommer-/Winterzeit keine Rolle spielt.
 * - Arbeitstag = Montag bis Freitag und kein gesetzlicher Feiertag in Bayern. Mariä Himmelfahrt (15.08.) zählt als
 *   Feiertag (Firmensitz in katholisch geprägter Gemeinde), das Augsburger Friedensfest nicht; 24.12. und 31.12. sind
 *   Arbeitstage.
 * - Ostern wird berechnet (gregorianischer Kalender), daher gilt der Kalender für jedes Jahr ab 1583.
 *
 * Wer Ergebnisse speichert, speichert `WORKDAYS_VERSION` mit (AD-27).
 */

export const WORKDAYS_VERSION = 'bavaria-v1';

export interface Holiday {
  /** ISO-Datum `YYYY-MM-DD`. */
  date: string;
  name: string;
}

const DAY_MS = 86_400_000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const FIRST_YEAR = 1583;

/** Wandelt ein ISO-Datum in einen fortlaufenden UTC-Tag; wirft bei ungültiger Eingabe. */
function toDay(date: string): number {
  const m = ISO_DATE.exec(String(date));
  if (!m) throw new RangeError(`Kein gültiges Datum (erwartet JJJJ-MM-TT): ${String(date)}`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const time = Date.UTC(year, month - 1, day);
  // Ungültige Tage (z. B. 30.02.) rollen in einen anderen Monat – daran erkennbar.
  if (year < FIRST_YEAR || new Date(time).getUTCMonth() !== month - 1) {
    throw new RangeError(`Kein gültiges Datum: ${date}`);
  }
  return time / DAY_MS;
}

function toIso(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

function checkYear(year: number): void {
  if (!Number.isInteger(year) || year < FIRST_YEAR || year > 9999) {
    throw new RangeError(`Kein gültiges Jahr: ${year}`);
  }
}

/** Ostersonntag (gregorianisch, Gauß/Meeus-Jones-Butcher) als ISO-Datum. */
export function easterSunday(year: number): string {
  checkYear(year);
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return toIso(Date.UTC(year, month - 1, day) / DAY_MS);
}

const FIXED: readonly (readonly [string, string])[] = [
  ['01-01', 'Neujahr'],
  ['01-06', 'Heilige Drei Könige'],
  ['05-01', 'Tag der Arbeit'],
  ['08-15', 'Mariä Himmelfahrt'],
  ['10-03', 'Tag der Deutschen Einheit'],
  ['11-01', 'Allerheiligen'],
  ['12-25', '1. Weihnachtstag'],
  ['12-26', '2. Weihnachtstag'],
];

/** Abstand in Tagen zum Ostersonntag. */
const MOVABLE: readonly (readonly [number, string])[] = [
  [-2, 'Karfreitag'],
  [1, 'Ostermontag'],
  [39, 'Christi Himmelfahrt'],
  [50, 'Pfingstmontag'],
  [60, 'Fronleichnam'],
];

/** Gesetzliche Feiertage in Bayern für ein Jahr, nach Datum sortiert. */
export function holidaysBavaria(year: number): Holiday[] {
  const easter = toDay(easterSunday(year));
  const list: Holiday[] = [
    ...FIXED.map(([md, name]) => ({ date: `${year}-${md}`, name })),
    ...MOVABLE.map(([offset, name]) => ({ date: toIso(easter + offset), name })),
  ];
  return list.sort((x, y) => x.date.localeCompare(y.date));
}

/**
 * Feiertage je Jahr als Nachschlag-Tabelle (UTC-Tag → Name), einmal je Jahr gebildet: `isWorkday`/`nextWorkday` laufen
 * in Vorschauen für zehntausende Posten (Story 18.27, Lasttest); das Ergebnis ist dasselbe wie `holidaysBavaria`.
 */
const holidayTables = new Map<number, ReadonlyMap<number, string>>();
function holidayAt(day: number): string | null {
  const year = new Date(day * DAY_MS).getUTCFullYear();
  let table = holidayTables.get(year);
  if (table === undefined) {
    table = new Map(holidaysBavaria(year).map((h) => [toDay(h.date), h.name]));
    holidayTables.set(year, table);
  }
  return table.get(day) ?? null;
}

/** Arbeitstag am UTC-Tag `day`: Montag bis Freitag und kein Feiertag in Bayern. */
function isWorkdayAt(day: number): boolean {
  const weekday = new Date(day * DAY_MS).getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  return holidayAt(day) === null;
}

/** Feiertag in Bayern? Gibt den Namen zurück, sonst `null`. */
export function holidayName(date: string): string | null {
  return holidayAt(toDay(date));
}

/** Montag bis Freitag und kein Feiertag in Bayern. */
export function isWorkday(date: string): boolean {
  return isWorkdayAt(toDay(date));
}

/** Frühester Arbeitstag ab einschließlich `date` (ist `date` selbst Arbeitstag, kommt `date` zurück). */
export function nextWorkday(date: string): string {
  let day = toDay(date);
  while (!isWorkdayAt(day)) day += 1;
  return toIso(day);
}

/** `count` Arbeitstage nach `date` (der Starttag zählt nicht mit); `count` = 0 gibt `date` unverändert zurück. */
export function addWorkdays(date: string, count: number): string {
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new RangeError(`Anzahl Arbeitstage muss eine ganze Zahl ab 0 sein: ${count}`);
  }
  let day = toDay(date);
  let left = count;
  while (left > 0) {
    day += 1;
    if (isWorkdayAt(day)) left -= 1;
  }
  return toIso(day);
}

/** Anzahl Arbeitstage von `from` (einschließlich) bis `toExclusive` (ausschließlich). */
export function workdaysBetween(from: string, toExclusive: string): number {
  const start = toDay(from);
  const end = toDay(toExclusive);
  if (end < start) {
    throw new RangeError(`Enddatum ${toExclusive} liegt vor dem Startdatum ${from}`);
  }
  let count = 0;
  for (let day = start; day < end; day += 1) {
    if (isWorkdayAt(day)) count += 1;
  }
  return count;
}

/** Lieferzeit als Zahl + Einheit (Arbeitstage oder Wochen), wie am Lieferanten-Artikel hinterlegt. */
export interface LeadTime {
  /** Ganze Zahl ab 0; `null` = keine Lieferzeit hinterlegt. */
  value: number | null;
  unit: 'workdays' | 'weeks';
}

/**
 * Lieferzeit in Arbeitstagen: Arbeitstage bleiben, Wochen × `workdaysPerWeek` (Einstellung
 * `purchasing_reorder_workdays_per_week`, nie Konstante). `value` null → `null`.
 * Wirft bei `value` < 0 oder nicht ganzzahlig, bei unbekannter Einheit und bei `workdaysPerWeek` außerhalb 1–7.
 */
export function leadTimeWorkdays(leadTime: LeadTime, workdaysPerWeek: number): number | null {
  if (!Number.isInteger(workdaysPerWeek) || workdaysPerWeek < 1 || workdaysPerWeek > 7) {
    throw new RangeError(`Arbeitstage je Woche müssen eine ganze Zahl von 1 bis 7 sein: ${workdaysPerWeek}`);
  }
  const { value, unit } = leadTime;
  if (value === null) return null;
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`Lieferzeit muss eine ganze Zahl ab 0 sein: ${value}`);
  }
  if (unit === 'workdays') return value;
  if (unit === 'weeks') return value * workdaysPerWeek;
  throw new RangeError(`Unbekannte Einheit der Lieferzeit: ${String(unit)}`);
}

/**
 * Datum nach Ablauf der Lieferzeit ab `date` im Kalender Bayern (= `addWorkdays(date, leadTimeWorkdays(…))`).
 * `value` null → `null`; Fehler wie `leadTimeWorkdays` und `addWorkdays`.
 */
export function addLeadTime(date: string, leadTime: LeadTime, workdaysPerWeek: number): string | null {
  const workdays = leadTimeWorkdays(leadTime, workdaysPerWeek);
  if (workdays === null) {
    toDay(date);
    return null;
  }
  return addWorkdays(date, workdays);
}
